import { buildTitle, clampDescription } from "@/lib/seo-quality";
import { Suspense, useEffect, useState, type ReactNode } from "react";
import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { BLOG_REDIRECTS } from "@/data/blog-redirects";
import { ArrowLeft, ArrowUpRight, BadgeCheck, Share2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { getPublishedPost, getRelatedPosts, listPublishedPosts } from "@/lib/blog-public.functions";
import { autoInternalLinks, type LinkTarget } from "@/lib/auto-internal-links";
import { listForumPosts } from "@/lib/forum.functions";



type RelatedPost = {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  meta_description: string | null;
  cover_url: string | null;
  published_at: string | null;
  reading_minutes: number | null;
};

import { BlogComments } from "@/components/BlogComments";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { AdSlot } from "@/components/AdSlot";
import { ShareButtons } from "@/components/ShareButtons";
import { RelatedLinks } from "@/components/RelatedLinks";
import { FyndanuGrid } from "@/components/FyndanuGrid";
import { StickyCta } from "@/components/StickyCta";
import { ReadingProgress } from "@/components/ReadingProgress";
import { TableOfContents } from "@/components/TableOfContents";
import { BlogTcoCalculator } from "@/components/BlogTcoCalculator";

function h2Slug(children: ReactNode): string {
  const text = (Array.isArray(children) ? children : [children])
    .map((c) => (typeof c === "string" ? c : typeof c === "number" ? String(c) : ""))
    .join("")
    .trim();
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 80);
}
const mdComponents = {
  h2: ({ children, ...props }: { children?: ReactNode }) => (
    <h2 id={h2Slug(children)} {...props}>
      {children}
    </h2>
  ),
  table: ({ children, ...props }: { children?: ReactNode }) => (
    <div className="not-prose my-8 -mx-4 md:mx-0 overflow-x-auto rounded-2xl border border-border bg-card/60">
      <table className="w-full min-w-[560px] border-collapse text-sm md:text-[15px]" {...props}>
        {children}
      </table>
    </div>
  ),
  thead: ({ children, ...props }: { children?: ReactNode }) => (
    <thead className="bg-secondary/60 text-left" {...props}>{children}</thead>
  ),
  th: ({ children, ...props }: { children?: ReactNode }) => (
    <th className="px-4 py-3 font-display text-xs uppercase tracking-wider text-foreground/80 border-b border-border" {...props}>{children}</th>
  ),
  td: ({ children, ...props }: { children?: ReactNode }) => (
    <td className="px-4 py-3 align-top border-b border-border/60 text-foreground/85" {...props}>{children}</td>
  ),
};

// AdSense ad-unit slot-ID:n — skapa två "In-article"-annonsenheter i AdSense
// och byt ut nedan. Lämnas de tomma renderas ingen manuell annons (Auto Ads
// via scriptet i __root.tsx fortsätter fungera).
const AD_SLOT_MID = "";
const AD_SLOT_BOTTOM = "";

export const Route = createFileRoute("/blogg/$slug")({
  beforeLoad: ({ params }) => {
    const target = BLOG_REDIRECTS[params.slug];
    if (target) {
      throw redirect({
        to: "/blogg/$slug",
        params: { slug: target },
        statusCode: 301,
        replace: true,
      });
    }
  },
  loader: async ({ params }) => {
    const { post } = await getPublishedPost({ data: { slug: params.slug } });
    if (!post) throw notFound();
    const [{ posts: related }, all, forum] = await Promise.all([
      getRelatedPosts({
        data: { slug: params.slug, keywords: (post.keywords ?? []).slice(0, 12) },
      }),
      listPublishedPosts().catch(() => ({ posts: [] as any[] })),
      listForumPosts({ data: { sort: "hot", limit: 40 } }).catch(() => ({ posts: [] as any[] })),
    ]);
    const ordered = (all?.posts ?? []).sort(
      (a: any, b: any) =>
        new Date(b.published_at ?? 0).getTime() - new Date(a.published_at ?? 0).getTime(),
    );
    const currentIndex = ordered.findIndex((p: any) => p.slug === params.slug);
    const prev = ordered[currentIndex + 1] as RelatedPost | undefined;
    const next = ordered[currentIndex - 1] as RelatedPost | undefined;

    const forumTargets = (forum?.posts ?? []).map((t: any) => ({
      href: `/forum/t/${t.slug}`,
      phrases: [String(t.title ?? "").replace(/\?+$/, "").trim()],
    }));

    const linked = {
      ...post,
      content: autoInternalLinks(post.content ?? "", ordered as LinkTarget[], {
        currentSlug: params.slug,
        maxLinks: 14,
        maxPerParagraph: 2,
        extraTargets: forumTargets,
      }),
    };
    const forumThreads = (forum?.posts ?? []).slice(0, 3).map((t: any) => ({
      slug: t.slug as string,
      title: t.title as string,
      comment_count: (t.comment_count ?? 0) as number,
    }));
    return { post: linked, related: (related ?? []) as RelatedPost[], prev, next, forumThreads };
  },



  head: ({ loaderData, params }) => {
    const post = loaderData?.post;
    if (!post) {
      return {
        meta: [
          { title: "Inlägget hittades inte – AK Husbil & Fritid" },
          { name: "robots", content: "noindex, follow" },
        ],
      };
    }
    const path = `https://www.akhusbilfritid.se/blogg/${encodeURIComponent(params.slug)}`;
    const desc = clampDescription(
      post.meta_description ??
      post.excerpt ??
        (post.content ? post.content.slice(0, 200).replace(/\s+/g, " ").trim() : ""),
    );
    const image = post.cover_url || undefined;
    const keywordsAttr = post.keywords && post.keywords.length ? post.keywords.join(", ") : undefined;
    const authorName = post.author_name ?? "AK Husbil & Fritid – Redaktionen";
    return {
      meta: [
        { title: buildTitle(post.title) },
        { name: "description", content: desc },
        { name: "author", content: authorName },
        ...(keywordsAttr ? [{ name: "keywords", content: keywordsAttr }] : []),
        { property: "og:type", content: "article" },
        { property: "og:title", content: post.title },
        { property: "og:description", content: desc },
        { property: "og:url", content: path },
        { property: "og:site_name", content: "AK Husbil & Fritid" },
        { property: "og:locale", content: "sv_SE" },
        ...(image
          ? [
              { property: "og:image", content: image },
              { name: "twitter:image", content: image },
              { name: "twitter:card", content: "summary_large_image" },
            ]
          : []),
        ...(post.published_at
          ? [{ property: "article:published_time", content: post.published_at }]
          : []),
        ...(post.updated_at
          ? [{ property: "article:modified_time", content: post.updated_at }]
          : []),
        { property: "article:author", content: authorName },
        { property: "article:section", content: "Husbil & Husvagn" },
      ],
      links: [{ rel: "canonical", href: path }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: desc,
            image: image ? [image] : undefined,
            datePublished: post.published_at ?? undefined,
            dateModified: post.updated_at ?? post.published_at ?? undefined,
            articleBody: post.content ?? undefined,
            wordCount: post.content ? post.content.trim().split(/\s+/).length : undefined,
            keywords: post.keywords && post.keywords.length ? post.keywords : undefined,
            timeRequired: post.reading_minutes ? `PT${post.reading_minutes}M` : undefined,
            author: {
              "@type": "Person",
              name: authorName,
              jobTitle: post.author_title ?? undefined,
              description: post.author_bio ?? undefined,
              image: post.author_avatar_url ?? undefined,
              url: "https://www.akhusbilfritid.se/om-oss",
              worksFor: {
                "@type": "Organization",
                name: "AK Husbil & Fritid",
                url: "https://www.akhusbilfritid.se",
              },
            },
            reviewedBy: {
              "@type": "Organization",
              name: "AK Husbil & Fritid – verkstad & säljteam",
              url: "https://www.akhusbilfritid.se/om-oss",
              knowsAbout: [
                "husbil",
                "husvagn",
                "fukttest",
                "gasoltest",
                "service och reservdelar",
              ],
            },

            publisher: {
              "@type": "Organization",
              name: "AK Husbil & Fritid",
              url: "https://www.akhusbilfritid.se",
              logo: {
                "@type": "ImageObject",
                url: "https://www.akhusbilfritid.se/og-image.jpg",
              },
            },
            mainEntityOfPage: { "@type": "WebPage", "@id": path },
            inLanguage: "sv-SE",
            isPartOf: {
              "@type": "Blog",
              "@id": "https://www.akhusbilfritid.se/blogg#blog",
              name: "AK Husbil & Fritid – Blogg",
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se/" },
              { "@type": "ListItem", position: 2, name: "Blogg", item: "https://www.akhusbilfritid.se/blogg" },
              { "@type": "ListItem", position: 3, name: post.title, item: path },
            ],
          }),
        },
        ...(Array.isArray(post.faq) && (post.faq as Array<{ q: string; a: string }>).length
          ? [{
              type: "application/ld+json" as const,
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: (post.faq as Array<{ q: string; a: string }>).map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              }),
            }]
          : []),
        ...(params.slug === "fyndanu-partner-trygg-husbilsaffar-online-2026"
          ? [
              {
                type: "application/ld+json" as const,
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "HowTo",
                  name: "Så köper du husbil tryggt online via Fyndanu",
                  description: "En steg-för-steg-guide för att köpa begagnad husbil online med fuktkontroll, videovisning och säker betalning.",
                  totalTime: "PT2H",
                  estimatedCost: { "@type": "MonetaryAmount", currency: "SEK", value: "1495-3500" },
                  step: [
                    { "@type": "HowToStep", position: 1, name: "Sätt budget inklusive reservkassa", text: "Räkna med 10 000–25 000 kr det första året för oförutsedda kostnader som batterier, markis och service." },
                    { "@type": "HowToStep", position: 2, name: "Filtrera på körkort och totalvikt", text: "B-kort räcker upp till 3 500 kg totalvikt. Tyngre fordon kräver B96, BE eller C1." },
                    { "@type": "HowToStep", position: 3, name: "Läs annonsen noga", text: "Kontrollera årsmodell, mätarställning, senaste service, fuktprotokoll, gasolbesiktning och kamrem." },
                    { "@type": "HowToStep", position: 4, name: "Boka gratis videovisning", text: "Se interiören live, ställ frågor och spara en resa om bilen inte känns rätt." },
                    { "@type": "HowToStep", position: 5, name: "Gör fysisk visning eller certifierad besiktning", text: "Antingen besök Kalix eller boka Bilprovningen där fordonet står." },
                    { "@type": "HowToStep", position: 6, name: "Skriv köpekontrakt med öppen köpsumma", text: "Betala aldrig förskott till en okänd säljare utan escrow eller kontant vid överlämning." },
                    { "@type": "HowToStep", position: 7, name: "Kontrollera servicehistorik", text: "Servicebok, verkstadsfakturor och tidigare ägare ska följa med." },
                    { "@type": "HowToStep", position: 8, name: "Ordna leverans eller hämta själv", text: "Vi levererar i Sverige och norra Norge. Räkna med 8–35 kr/km beroende på sträcka." },
                  ],
                }),
              },
              {
                type: "application/ld+json" as const,
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "VideoObject",
                  name: "Så känns en trygg husbilsaffär online – AK Husbil & Fritid × Fyndanu",
                  description: "Cinematiskt inifrån en husbil i Kalix. Så ser en trygg, digital husbilsaffär ut 2026.",
                  thumbnailUrl: ["https://www.akhusbilfritid.se/blog/fyndanu-partner-hero.jpg"],
                  uploadDate: "2026-07-11",
                  contentUrl: "https://www.akhusbilfritid.se/blog/fyndanu-trust-story.mp4",
                  duration: "PT10S",
                  publisher: {
                    "@type": "Organization",
                    name: "AK Husbil & Fritid",
                    logo: { "@type": "ImageObject", url: "https://www.akhusbilfritid.se/og-image.jpg" },
                  },
                }),
              },
              {
                type: "application/ld+json" as const,
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "WebPage",
                  "@id": `${path}#speakable`,
                  speakable: {
                    "@type": "SpeakableSpecification",
                    cssSelector: ["h1", ".eyebrow + ul", "aside[aria-label='Kort sammanfattning']"],
                  },
                  url: path,
                }),
              },
            ]
          : []),
      ],
    };
  },
  component: PostPage,
  notFoundComponent: () => (
    <div className="container-edge py-32 text-center">
      <p className="eyebrow">404</p>
      <h1 className="mt-4 font-display text-5xl md:text-7xl">
        Inlägget finns <span className="font-italic-display text-accent">inte</span>.
      </h1>
      <Link
        to="/blogg"
        className="mt-10 inline-flex items-center gap-2 btn-shine rounded-full bg-primary px-6 py-3.5 text-sm font-medium text-primary-foreground"
      >
        Till bloggen
      </Link>
    </div>
  ),
  errorComponent: ({ error }: { error: unknown }) => (
    <div className="container-edge py-32 text-center">
      <h1 className="font-display text-3xl">Något gick fel</h1>
      <p className="mt-2 text-sm text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
    </div>
  ),
});

function PostPage() {
  const { post, related, prev, next, forumThreads } = Route.useLoaderData() as {
    post: any;
    related: RelatedPost[];
    prev?: RelatedPost;
    next?: RelatedPost;
    forumThreads?: Array<{ slug: string; title: string; comment_count: number }>;
  };



  return (
    <article>
      <ReadingProgress />
      <div className="container-edge py-16 md:py-24">
        <div className="grid grid-cols-1 gap-12 xl:grid-cols-[minmax(0,3fr)_320px]">
          <main className="max-w-3xl">
            <Link to="/blogg" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-accent">
              <ArrowLeft size={14} /> Tillbaka till bloggen
            </Link>

        <div className="mt-12 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-medium uppercase tracking-wider text-accent">
          {post.published_at && (
            <time dateTime={post.published_at}>
              {new Date(post.published_at).toLocaleDateString("sv-SE", { year: "numeric", month: "long", day: "numeric" })}
            </time>
          )}
          {post.reading_minutes ? (
            <>
              <span aria-hidden className="text-muted-foreground/60">·</span>
              <span>{post.reading_minutes} min läsning</span>
            </>
          ) : null}
        </div>

        <h1 className="mt-4 font-display text-5xl leading-[1.02] md:text-7xl">{post.title}</h1>
        {post.excerpt && (
          <p className="mt-6 text-xl leading-relaxed text-muted-foreground md:text-2xl">{post.excerpt}</p>
        )}

        {post.author_name && (
          <div className="mt-8 flex items-center gap-4 rounded-2xl border border-border bg-card/60 p-4">
            {post.author_avatar_url ? (
              <img
                src={post.author_avatar_url}
                alt={post.author_name}
                loading="lazy"
                className="h-12 w-12 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary font-display text-lg">
                AK
              </div>
            )}
            <div className="min-w-0">
              <p className="font-medium leading-tight">{post.author_name}</p>
              {post.author_title && (
                <p className="text-xs text-muted-foreground">{post.author_title}</p>
              )}
            </div>
          </div>
        )}

        <p className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <BadgeCheck className="h-4 w-4 shrink-0 text-emerald-500" aria-hidden />
          <span>
            Faktagranskad av vår verkstad &amp; säljteam i Kalix
            {post.updated_at
              ? ` · uppdaterad ${new Date(post.updated_at).toLocaleDateString("sv-SE")}`
              : ""}
            {" · "}
            <Link to="/om-oss" className="underline underline-offset-2 hover:text-foreground">
              Om oss
            </Link>
          </span>
        </p>


        <ShareButtons title={post.title} path={`/blogg/${post.slug}`} />

        {post.cover_url && (
          <div className="mt-10 overflow-hidden rounded-3xl elev-shadow">
            <img
              src={post.cover_url}
              alt={post.title}
              width={1600}
              height={900}
              fetchPriority="high"
              decoding="async"
              className="aspect-[16/9] h-auto w-full object-cover"
            />
          </div>
        )}

        {(() => {
          let content = post.content ?? "";
          // Strip a leading markdown image if it duplicates the cover
          content = content.replace(/^\s*!\[[^\]]*\]\([^)]+\)\s*\n+/, "");

          // Tokenize content around widget markers so we can inject rich blocks
          const TOKEN_RE = /^:::(fyndanu-live|tco-calc):::\s*$/gm;
          type Token = { type: "md"; body: string } | { type: "widget"; name: string };
          const tokens: Token[] = [];
          let lastIndex = 0;
          for (const m of content.matchAll(TOKEN_RE)) {
            const idx = m.index ?? 0;
            const before = content.slice(lastIndex, idx);
            if (before.trim()) tokens.push({ type: "md", body: before });
            tokens.push({ type: "widget", name: m[1] });
            lastIndex = idx + m[0].length;
          }
          const tail = content.slice(lastIndex);
          if (tail.trim()) tokens.push({ type: "md", body: tail });
          if (tokens.length === 0) tokens.push({ type: "md", body: content });

          const proseCls =
            "prose prose-lg max-w-none prose-headings:font-display prose-headings:tracking-tight prose-h2:mt-16 prose-h2:text-4xl prose-h3:mt-10 prose-h3:text-2xl prose-p:leading-relaxed prose-p:text-foreground/85 prose-a:text-accent prose-a:no-underline hover:prose-a:underline prose-strong:text-foreground prose-img:rounded-3xl prose-img:elev-shadow prose-blockquote:border-l-4 prose-blockquote:border-accent prose-blockquote:not-italic prose-blockquote:font-display prose-blockquote:text-2xl prose-blockquote:text-foreground prose-li:text-foreground/85";

          const renderSegment = (md: string, key: string, withMidAd = false) => {
            const blocks = md.split(/\n\n+/);
            const mid = Math.max(1, Math.floor(blocks.length / 2));
            const first = blocks.slice(0, mid).join("\n\n");
            const rest = blocks.slice(mid).join("\n\n");
            return (
              <div key={key}>
                <div className={`${proseCls} mt-12`}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={mdComponents}>{first}</ReactMarkdown>
                </div>
                {withMidAd && <AdSlot variant="in-article-2" slot={AD_SLOT_MID} />}
                {rest && (
                  <div className={proseCls}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={mdComponents}>{rest}</ReactMarkdown>
                  </div>
                )}
              </div>
            );
          };

          const renderWidget = (name: string, key: string) => {
            if (name === "fyndanu-live") {
              return (
                <section key={key} aria-label="Aktuella husbilar hos Fyndanu" className="mt-12">
                  <p className="eyebrow text-accent">Live · Fyndanu</p>
                  <h2 className="mt-3 font-display text-3xl leading-tight md:text-4xl">
                    Aktuella <span className="font-italic-display text-accent">fordon</span> just nu
                  </h2>
                  <p className="mt-3 text-sm text-muted-foreground">
                    Uppdateras direkt från vår butik på Fyndanu.se.
                  </p>
                  <div className="mt-6">
                    <Suspense fallback={<div className="h-64 animate-pulse rounded-3xl bg-card" />}>
                      <FyndanuGrid />
                    </Suspense>
                  </div>
                </section>
              );
            }
            if (name === "tco-calc") {
              return <BlogTcoCalculator key={key} />;
            }
            return null;
          };

          return (
            <>
              {Array.isArray(post.tldr) && (post.tldr as string[]).length > 0 && (
                <aside
                  aria-label="Kort sammanfattning"
                  className="mt-12 rounded-3xl border border-accent/25 bg-accent/5 p-6 md:p-7"
                >
                  <p className="eyebrow text-accent">Kort sagt</p>
                  <ul className="mt-3 space-y-2">
                    {(post.tldr as string[]).map((t, i) => (
                      <li key={i} className="flex gap-3 text-[15px] leading-relaxed text-foreground/90">
                        <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </aside>
              )}
              <TableOfContents markdown={content} />
              {tokens.map((tok, i) =>
                tok.type === "md"
                  ? renderSegment(tok.body, `tok-${i}`, i === 0)
                  : renderWidget(tok.name, `tok-${i}`),
              )}
              {Array.isArray(post.faq) && (post.faq as Array<{ q: string; a: string }>).length > 0 && (
                <section aria-labelledby="faq-heading" className="mt-16">
                  <p className="eyebrow">FAQ</p>
                  <h2 id="faq-heading" className="mt-3 font-display text-4xl leading-tight md:text-5xl">
                    Vanliga <span className="font-italic-display text-accent">frågor</span>
                  </h2>
                  <div className="mt-8 divide-y divide-border rounded-3xl border border-border bg-card/60">
                    {(post.faq as Array<{ q: string; a: string }>).map((f, i) => (
                      <details key={i} className="group p-6 md:p-7">
                        <summary className="flex cursor-pointer list-none items-start justify-between gap-6 font-display text-xl leading-snug">
                          <span>{f.q}</span>
                          <span
                            aria-hidden
                            className="mt-1 h-6 w-6 shrink-0 rounded-full border border-border text-center leading-[22px] text-muted-foreground transition group-open:rotate-45 group-open:border-accent group-open:text-accent"
                          >
                            +
                          </span>
                        </summary>
                        <p className="mt-4 text-[15px] leading-relaxed text-foreground/80">{f.a}</p>
                      </details>
                    ))}
                  </div>
                </section>
              )}
            </>
          );
        })()}

        <NewsletterSignup
          variant="inline"
          source="blog-post"
          title="Gillar du sånt här?"
          description="Få nya guider, husbilar i lager och rese­tips i inkorgen. ~1 mejl/månad."
        />

        <AdSlot variant="display" slot={AD_SLOT_BOTTOM} />

        {post.author_bio && (
          <aside className="mt-16 rounded-3xl border border-border bg-card p-6 md:p-8">
            <p className="eyebrow">Om skribenten</p>
            <div className="mt-4 flex items-start gap-5">
              {post.author_avatar_url && (
                <img
                  src={post.author_avatar_url}
                  alt={post.author_name ?? ""}
                  loading="lazy"
                  className="h-16 w-16 shrink-0 rounded-full object-cover"
                />
              )}
              <div>
                <p className="font-display text-2xl leading-tight">{post.author_name}</p>
                {post.author_title && (
                  <p className="mt-1 text-sm text-muted-foreground">{post.author_title}</p>
                )}
                <p className="mt-3 text-sm leading-relaxed text-foreground/85">{post.author_bio}</p>
              </div>
            </div>
          </aside>
        )}

        {forumThreads && forumThreads.length > 0 && (
          <section aria-labelledby="forum-cross-heading" className="mt-14 rounded-3xl border border-border bg-card/60 p-6 md:p-8">
            <p className="eyebrow text-accent">Fritidsforum</p>
            <h2 id="forum-cross-heading" className="mt-2 font-display text-2xl leading-tight md:text-3xl">
              Fråga andra <span className="font-italic-display text-accent">husbilsägare</span>
            </h2>
            <ul className="mt-5 grid gap-3 sm:grid-cols-3">
              {forumThreads.map((t) => (
                <li key={t.slug} className="min-w-0">
                  <Link
                    to="/forum/t/$slug"
                    params={{ slug: t.slug }}
                    className="block h-full rounded-2xl border border-border bg-background p-4 transition hover:border-accent/50"
                  >
                    <span className="line-clamp-3 text-sm font-medium leading-snug text-foreground">{t.title}</span>
                    <span className="mt-2 block text-xs text-muted-foreground">{t.comment_count} svar</span>
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              to="/forum"
              className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
            >
              Till Fritidsforum <ArrowUpRight size={15} />
            </Link>
          </section>
        )}


        {related.length > 0 && (
          <section aria-labelledby="related-heading" className="mt-16">
            <p className="eyebrow text-accent">Läs vidare</p>
            <h2 id="related-heading" className="mt-3 font-display text-3xl leading-tight md:text-4xl">
              Mer i samma <span className="font-italic-display text-accent">spår</span>
            </h2>
            <div className="mt-7 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((r) => (
                <Link
                  key={r.id}
                  to="/blogg/$slug"
                  params={{ slug: r.slug }}
                  className="group flex flex-col overflow-hidden rounded-3xl border border-border bg-card hover-lift"
                >
                  {r.cover_url ? (
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={r.cover_url}
                        alt={r.title}
                        loading="lazy"
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                      />
                    </div>
                  ) : (
                    <div className="aspect-[16/10] bg-secondary" />
                  )}
                  <div className="flex flex-1 flex-col p-5">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-xs font-medium uppercase tracking-wider text-accent">
                        {r.published_at ? new Date(r.published_at).toLocaleDateString("sv-SE") : "Guide"}
                        {r.reading_minutes ? ` · ${r.reading_minutes} min` : ""}
                      </p>
                      <ArrowUpRight size={16} className="shrink-0 text-muted-foreground transition group-hover:text-foreground" />
                    </div>
                    <h3 className="mt-2 font-display text-xl leading-snug">{r.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <RelatedLinks
          keyId={post.slug}
          text={`${post.title} ${post.focus_keyword ?? ""} ${(post.keywords ?? []).join(" ")} ${String(post.content ?? "").slice(0, 4000)}`}
          currentHref={`/blogg/${post.slug}`}
        />

        <BlogComments postId={post.id} />
        {(prev || next) && <PostNav prev={prev} next={next} />}
      </main>
      <aside className="hidden xl:block">
        <TableOfContents markdown={post.content ?? ""} sticky />
      </aside>
    </div>
    </div>

      <FloatingShare title={post.title} path={`/blogg/${post.slug}`} />
      <StickyCta
        href="https://www.fyndanu.se/butik/e435da0b-10d0-46fa-a389-702295c89840"
        label="Aktuella husbilar & husvagnar"
        cta="Se våra annonser på Fyndanu.se — uppdateras varannan timme"
      />
    </article>
  );
}

function PostNav({ prev, next }: { prev?: RelatedPost; next?: RelatedPost }) {
  return (
    <nav aria-label="Föregående och nästa inlägg" className="mt-16 grid gap-4 sm:grid-cols-2">
      {prev ? (
        <Link
          to="/blogg/$slug"
          params={{ slug: prev.slug }}
          className="group flex flex-col rounded-3xl border border-border bg-card p-5 transition hover-lift"
        >
          <span className="text-xs uppercase tracking-wider text-muted-foreground">← Föregående</span>
          <span className="mt-2 font-display text-lg leading-snug">{prev.title}</span>
        </Link>
      ) : (
        <div />
      )}
      {next ? (
        <Link
          to="/blogg/$slug"
          params={{ slug: next.slug }}
          className="group flex flex-col rounded-3xl border border-border bg-card p-5 text-right transition hover-lift sm:items-end"
        >
          <span className="text-xs uppercase tracking-wider text-muted-foreground">Nästa →</span>
          <span className="mt-2 font-display text-lg leading-snug">{next.title}</span>
        </Link>
      ) : (
        <div />
      )}
    </nav>
  );
}

function FloatingShare({ title, path }: { title: string; path: string }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const url = `https://www.akhusbilfritid.se${path}`;
  const native = async () => {
    if (typeof navigator !== "undefined" && "share" in navigator) {
      try {
        await (navigator as Navigator & { share: (d: ShareData) => Promise<void> }).share({ title, url });
        return;
      } catch {
        /* fall through */
      }
    }
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      /* noop */
    }
  };

  if (!visible) return null;
  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-card/95 px-4 py-3 shadow-[0_-8px_24px_-12px_rgb(20_14_8_/_0.18)] backdrop-blur md:hidden">
      <div className="container-edge flex items-center justify-between gap-4">
        <span className="truncate text-sm font-medium">Dela: {title}</span>
        <button
          type="button"
          onClick={native}
          className="inline-flex shrink-0 items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
        >
          <Share2 size={16} /> Dela
        </button>
      </div>
    </div>
  );
}
